<template>
  <div class="rate-details">
    <div ref="header" class="rate-details-header">
      <header-close :deactivate="deactivate" />
      <div class="title">{{`${room.title} - ${rate.title}`}}</div>
    </div>
    <div class="rate-details-scroll" :style="[{ height: scrollHeight + 'px' }]">
      <div class="rate-img">
        <picture class="image-wrapper"
                 v-lazyload="{
              src: rateImg.src,
              set: [
                { width: '640', height: '307', breakpoint: 'xs' },
                { width: '800', height: '412', breakpoint: 'lg' }
              ],
              fit: 'crop',
              align: 'center'
            }"
        >
          <img
              class="image"
              src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
              v-bind:alt="rateImg.alt"
          />
        </picture>
      </div>
      <div class="rate-details-info">
        <div class="title">{{ rate.title || 'Our best rate' }}</div>
        <div class="description short" v-html="rate.shortDescription" />
        <div class="description" v-html="rate.longDescription" />
      </div>
      <div class="price-breakdown-main">
        <div class="section-label">
          {{tc('booking.priceBreakdown')}}
        </div>
        <div class="overview-item">
          <div class="item-label bold-item">
            {{rate.title || 'Our best rate'}}
          </div>
          <div class="item-value">
            <div>{{avgPrice}}</div>
            <div class="section-label sub no-margin">
              {{tc('booking.avgPerNight')}}
            </div>
          </div>
        </div>
        <div class="overview-item nightly-rates">
          <div class="item-label bold-item">
            {{rate.nightlyRates.length}} {{rate.nightlyRates.length > 1 ? tc('noums.nightPlural') : tc('noums.nightSingular')}}
          </div>
          <div class="item-value bold-item">
            {{getDisplayPrice({price: totalPerNight})}}
          </div>
        </div>
        <overview-nightly-rates
          :nightly-rates="rate.nightlyRates"
        />
      </div>
      <div class="price-breakdown-taxes">
        <div class="section-label">
          {{ tc('booking.taxesAndFees') }}
        </div>
        <div class="overview-item">
          <div class="item-label bold-item">
            {{tc('booking.total')}} {{tc('booking.taxesAndFees')}}
          </div>
          <div class="item-value bold-item">
            {{totalTaxes}}
          </div>
        </div>
        <div class="overview-items-wrapper">
          <div
            v-for="(item, i) in rate.taxesFees"
            :key="item.code + i"
            class="overview-item"
            :class="[
              { 'small-margin': i < rate.taxesFees.length - 1 },
              { 'no-margin': i === rate.taxesFees.length - 1 }
            ]"
          >
            <div class="item-label section-label">
              {{ item.description }}
            </div>
            <div class="item-value">
              {{ getDisplayPrice({price: item.price}) }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="rate.restrictionPolicy" class="restriction-policy">
        <div class="section-title">
          {{ tc('booking.restrictionPolicy') }}
        </div>
        <div class="section-body" v-html="rate.restrictionPolicy" />
      </div>
      <div class="terms-and-conditions">
        <div class="section-title">
          {{ tc('booking.termsAndConditions') }}
        </div>
        <div class="section-body" v-html="rate.termsAndConditions" />
      </div>
    </div>
    <div ref="footer" class="rate-details-footer rate-cta-wrapper">
      <div class="rate-price">
        <div class="price">
          <strong>{{tc('booking.total')}}</strong>
          <span> {{totalDisplay}}</span>
        </div>
      </div>
        <div
          class="rate-cta"
          :class="[
            { 'is-selecting': isLoading },
            { selected: isSelected }
          ]"
        >
          <button
            :disabled="isDisabled"
            @click="doCartAction"
            @keydown.space.prevent="doCartAction"
          >
            <span v-if="!isLoading">
              {{ !isSelected ? tc('booking.bookThisRate') : tc('booking.keepThisRate')}}
            </span>
            <loading-dots v-else />
          </button>
        </div>
    </div>
  </div>
</template>

<script>
import RateCardMixin from '@mixins/rate-card-mixin'
import TabTrapMixin from '@mixins/tab-trap'
import HeaderClose from '@components/04.booking/booking-widget/components/header-close/header-close.vue'
import OverviewNightlyRates from '@components/04.booking/booking-wizard/components/overview-nightly-rates/overview-nightly-rates.vue'
import { mapGetters, mapState } from 'vuex'
export default {
  name: 'rate-details',
  components: {
    HeaderClose,
    OverviewNightlyRates
  },
  props: {
    room: {
      type: Object,
      required: true
    }
  },
  mixins: [TabTrapMixin, RateCardMixin],
  data: () => ({
    scrollHeight: 'auto'
  }),
  computed: {
    ...mapState('responsive', ['height']),
    ...mapGetters('currency', ['currentCurrency']),
    ...mapGetters('booking', ['selectedHotel']),
    taxesAndFees() {
      return Number(this.rate.taxes) + Number(this.rate.fees)
    },
    totalDisplay() {
      return this.getDisplayPrice({price: this.rate.total, withIsoCode: true, isoAfter: !this.isSwiss})
    },
    totalPerNight() {
      return this.rate.nightlyRates.map(n => Number(n.price)).reduce((a, b) => a + b)
    },
    totalTaxes() {
      return this.getDisplayPrice({price: this.taxesAndFees, withIsoCode: true, isoAfter: !this.isSwiss})
    }
  },
  watch: {
    height() {
      this.calculateScroll()
    }
  },
  mounted() {
    this.calculateScroll()
  },
  methods: {
    calculateScroll() {
      const headerHeight = this.$refs.header?.getBoundingClientRect().height ?? 0
      const footerHeight = this.$refs.footer?.getBoundingClientRect().height ?? 0
      this.scrollHeight = this.height - headerHeight - footerHeight
    },
    deactivate() {
      this.$emit('close')
    }
  },
}
</script>
